import { queryOptions, type QueryClient } from "@tanstack/react-query";

import { destroyEngine } from "@/features/sync/engine";
import { deleteReplica } from "@/features/sync/persistence";
import { ApiError, api } from "@/lib/api";
import {
  AccessLinkPreviewSchema,
  API_PATHS,
  AuthProvidersSchema,
  MeResponseSchema,
  type LoginBody,
  type MeResponse,
  type RedeemAccessLinkBody,
} from "@/lib/contract";

/**
 * Who is signed in.
 *
 * The answer is cached on the device, and used when the server cannot be
 * reached. That is the local-first promise extended to start-up: open the
 * board on a train with no signal and it appears, from the replica already on
 * this machine, rather than a login page that cannot log anyone in. Only a
 * definite "no" from the server (401) signs a person out.
 *
 * The cache holds identity, not credentials. The session itself is an
 * httpOnly cookie that no script can read.
 */

const SESSION_KEY = "kb:session";

function remember(me: MeResponse): void {
  try {
    window.localStorage.setItem(SESSION_KEY, JSON.stringify(me));
  } catch {
    // Not persisted: offline start-up will not work, everything else will.
  }
}

function recall(): MeResponse | null {
  try {
    const raw = window.localStorage.getItem(SESSION_KEY);
    if (!raw) return null;
    const parsed = MeResponseSchema.safeParse(JSON.parse(raw));
    return parsed.success ? parsed.data : null;
  } catch {
    return null;
  }
}

function forget(): void {
  try {
    window.localStorage.removeItem(SESSION_KEY);
  } catch {
    // Nothing to remove.
  }
}

async function fetchMe(): Promise<MeResponse | null> {
  try {
    const me = await api(API_PATHS.me, { schema: MeResponseSchema });
    remember(me);
    return me;
  } catch (error) {
    if (error instanceof ApiError && error.status === 401) {
      forget();
      return null;
    }
    if (error instanceof ApiError && error.status === 0) {
      const cached = recall();
      if (cached) return cached;
    }
    throw error;
  }
}

export const meQuery = queryOptions({
  queryKey: ["me"],
  queryFn: fetchMe,
  staleTime: Number.POSITIVE_INFINITY,
  retry: false,
});

export const providersQuery = queryOptions({
  queryKey: ["auth", "providers"],
  queryFn: () => api(API_PATHS.providers, { schema: AuthProvidersSchema }),
  staleTime: 5 * 60_000,
  retry: false,
});

export async function signIn(queryClient: QueryClient, body: LoginBody): Promise<MeResponse> {
  const me = await api(API_PATHS.login, { method: "POST", body, schema: MeResponseSchema });
  remember(me);
  queryClient.setQueryData(meQuery.queryKey, me);
  return me;
}

/** Who an invitation or reset link is for, so the page can greet them by name. */
export const accessLinkPreviewQuery = (token: string) =>
  queryOptions({
    queryKey: ["auth", "access-link", token],
    queryFn: () =>
      api(API_PATHS.accessLinkPreview, {
        method: "POST",
        body: { token },
        schema: AccessLinkPreviewSchema,
      }),
    staleTime: Number.POSITIVE_INFINITY,
    retry: false,
  });

/** Set a password from a link; the server signs the person in as it answers. */
export async function redeemAccessLink(
  queryClient: QueryClient,
  body: RedeemAccessLinkBody,
): Promise<MeResponse> {
  const me = await api(API_PATHS.accessLink, { method: "POST", body, schema: MeResponseSchema });
  remember(me);
  queryClient.setQueryData(meQuery.queryKey, me);
  return me;
}

/**
 * Sign out, and leave nothing behind: the server session, the cached
 * identity, and this person's replica of the board. On a shared machine, the
 * next person to sit down must find nothing of the last one's work.
 */
export async function signOut(queryClient: QueryClient): Promise<void> {
  const me = queryClient.getQueryData(meQuery.queryKey);
  await api(API_PATHS.logout, { method: "POST" }).catch(() => undefined);
  await destroyEngine();
  if (me) await deleteReplica({ userId: me.user.id, workspaceId: me.workspace.id });
  forget();
  queryClient.setQueryData(meQuery.queryKey, null);
  queryClient.removeQueries({ predicate: (query) => query.queryKey[0] !== "me" });
}

/** The server ended the session (deactivated, or signed out everywhere). */
export async function sessionEnded(queryClient: QueryClient): Promise<void> {
  await destroyEngine();
  forget();
  queryClient.setQueryData(meQuery.queryKey, null);
}

/** Only relative paths: an absolute URL here is an open redirect. */
export function safeReturnTo(value: string | undefined): string {
  if (!value?.startsWith("/") || value.startsWith("//")) return "/";
  return value;
}
